import type {
	AddStudentWorkPayload,
	CreateCorrectionPayload,
	EvaluateCorrectionPayload,
	EvaluateStudentWorkPayload,
	UpdateCorrectionPayload,
	UpdateStudentWorkPayload,
} from '@kimuno/api-sdk';

import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';

const service = {
	uploadMaterial,
	deleteMaterial,
	getRemainingLimit,
	createCorrection,
	getCorrection,
	updateCorrection,
	updateCorrectionTitle,
	uploadStudentWorkFile,
	deleteStudentWorkFile,
	addStudentWork,
	removeStudentWork,
	renameStudentWork,
	getStudentWork,
	updateStudentWork,
	evaluateCorrection,
	evaluateStudentWork,
};

function uploadMaterial(formData: FormData, abortSignal?: AbortSignal) {
	notifyBugsnagNetwork('Corrections', 'uploadMaterial');
	return handleUnauthorized(() => api.corrections.uploadMaterial(formData, abortSignal));
}

function deleteMaterial(hashName: string) {
	notifyBugsnagNetwork('Corrections', 'deleteMaterial', { hashName });
	return handleUnauthorized(() => api.corrections.deleteMaterial(hashName));
}

function getRemainingLimit() {
	notifyBugsnagNetwork('Corrections', 'getRemainingLimit');
	return handleUnauthorized(() => api.corrections.getRemainingLimit());
}

function createCorrection(payload: CreateCorrectionPayload) {
	notifyBugsnagNetwork('Corrections', 'createCorrection', { ...payload });
	return handleUnauthorized(() => api.corrections.createCorrection(payload));
}

function getCorrection(correctionId: string) {
	notifyBugsnagNetwork('Corrections', 'getCorrection', { correctionId });
	return handleUnauthorized(() => api.corrections.getCorrection(correctionId));
}

function updateCorrection(correctionId: string, payload: UpdateCorrectionPayload) {
	notifyBugsnagNetwork('Corrections', 'updateCorrection', { correctionId });
	return handleUnauthorized(() => api.corrections.updateCorrection(correctionId, payload));
}

function updateCorrectionTitle(correctionId: string, title: string) {
	notifyBugsnagNetwork('Corrections', 'updateCorrectionTitle', { correctionId });
	return handleUnauthorized(() => api.corrections.updateCorrectionTitle(correctionId, { title }));
}

function uploadStudentWorkFile(formData: FormData, abortSignal?: AbortSignal) {
	notifyBugsnagNetwork('Corrections', 'uploadStudentWorkFile');
	return handleUnauthorized(() => api.corrections.uploadStudentWorkFile(formData, abortSignal));
}

function deleteStudentWorkFile(hashName: string) {
	notifyBugsnagNetwork('Corrections', 'deleteStudentWorkFile', { hashName });
	return handleUnauthorized(() => api.corrections.deleteStudentWorkFile(hashName));
}

function addStudentWork(correctionId: string, payload: AddStudentWorkPayload) {
	notifyBugsnagNetwork('Corrections', 'addStudentWork', {
		correctionId,
		fileCount: payload.files.length,
	});
	return handleUnauthorized(() => api.corrections.addStudentWork(correctionId, payload));
}

function removeStudentWork(correctionId: string, studentWorkId: string) {
	notifyBugsnagNetwork('Corrections', 'removeStudentWork', { correctionId, studentWorkId });
	return handleUnauthorized(() => api.corrections.removeStudentWork(correctionId, studentWorkId));
}

function renameStudentWork(correctionId: string, studentWorkId: string, pseudonym: string) {
	notifyBugsnagNetwork('Corrections', 'renameStudentWork', { correctionId, studentWorkId });
	return handleUnauthorized(() =>
		api.corrections.renameStudentWork(correctionId, studentWorkId, { pseudonym })
	);
}

function getStudentWork(correctionId: string, studentWorkId: string) {
	notifyBugsnagNetwork('Corrections', 'getStudentWork', { correctionId, studentWorkId });
	return handleUnauthorized(() => api.corrections.getStudentWork(correctionId, studentWorkId));
}

function updateStudentWork(
	correctionId: string,
	studentWorkId: string,
	payload: UpdateStudentWorkPayload
) {
	notifyBugsnagNetwork('Corrections', 'updateStudentWork', { correctionId, studentWorkId });
	return handleUnauthorized(() =>
		api.corrections.updateStudentWork(correctionId, studentWorkId, payload)
	);
}

function evaluateCorrection(correctionId: string, payload: EvaluateCorrectionPayload) {
	notifyBugsnagNetwork('Corrections', 'evaluateCorrection', { correctionId });
	return handleUnauthorized(() => api.corrections.evaluateCorrection(correctionId, payload));
}

function evaluateStudentWork(
	correctionId: string,
	studentWorkId: string,
	payload?: EvaluateStudentWorkPayload
) {
	notifyBugsnagNetwork('Corrections', 'evaluateStudentWork', { correctionId, studentWorkId });
	return handleUnauthorized(() =>
		api.corrections.evaluateStudentWork(correctionId, studentWorkId, payload)
	);
}

export default service;
